import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Shield, Sparkles, Hammer, Trees, Star, CheckCircle2 } from "lucide-react";
import heroImg from "@/assets/hero-epoxy.jpg?w=1100&format=webp&quality=52";
import metallicImg from "@/assets/service-metallic.jpg?w=560&format=webp&quality=52";
import hardscapeImg from "@/assets/service-hardscape.jpg?w=560&format=webp&quality=52";
import demoImg from "@/assets/service-demo.jpg?w=560&format=webp&quality=50";
import { CtaBanner } from "@/components/site/CtaBanner";
import { Reveal } from "@/components/site/Reveal";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "NJ Epoxy Flooring Contractor | Blanco Pro Services" },
      { name: "description", content: "Top-rated NJ epoxy flooring contractors. Metallic, flake, polyaspartic & polished concrete for garages, homes & businesses. Free estimate: 908-895-6133." },
      { property: "og:title", content: "NJ Epoxy Flooring Contractor | Blanco Pro Services" },
      { property: "og:description", content: "Top-rated NJ epoxy flooring contractors. Metallic, flake, polyaspartic & polished concrete for garages, homes & businesses. Free estimate: 908-895-6133." },

      { property: "og:url", content: "https://blancopros.com/" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/oDG2kkVtzvRcH5WSww6hSVWgrrj1/social-images/social-1784155261276-ChatGPT_Image_Jul_15,_2026,_06_40_40_PM.webp" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/oDG2kkVtzvRcH5WSww6hSVWgrrj1/social-images/social-1784155261276-ChatGPT_Image_Jul_15,_2026,_06_40_40_PM.webp" },
    ],
    links: [
      { rel: "canonical", href: "https://blancopros.com/" },
      { rel: "preload", as: "image", href: heroImg, fetchpriority: "high" },
    ],

    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://blancopros.com/#localbusiness",
          name: "Blanco Pro Services",
          description: "Epoxy flooring, hardscaping, and demolition contractor serving New Jersey, Pennsylvania, and New York.",
          url: "https://blancopros.com/",
          telephone: "+1-908-895-6133",
          email: "info@blancopros.com",
          image: "https://blancopros.com/og-image.jpg",
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            addressRegion: "NJ",
            addressCountry: "US",
          },
          areaServed: [
            { "@type": "State", name: "New Jersey" },
            { "@type": "State", name: "Pennsylvania" },
            { "@type": "State", name: "New York" },
          ],
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            opens: "07:30",
            closes: "16:30",
          }],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "5.0",
            reviewCount: "27",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "How long does an epoxy floor last?",
              acceptedAnswer: { "@type": "Answer", text: "A professionally installed residential epoxy floor lasts 15–25 years; commercial systems typically last 12–20 years with a mid-life topcoat refresh. Surface prep (diamond grinding, moisture testing, crack repair) is the single biggest factor in lifespan." },
            },
            {
              "@type": "Question",
              name: "What areas does Blanco Pro Services cover?",
              acceptedAnswer: { "@type": "Answer", text: "Blanco Pro Services serves every county in New Jersey, plus eastern Pennsylvania and parts of New York." },
            },
            {
              "@type": "Question",
              name: "How long does an epoxy floor installation take?",
              acceptedAnswer: { "@type": "Answer", text: "Most Blanco Pro epoxy projects are completed in a 1–3 day window. Polyaspartic systems allow return-to-service in roughly 24 hours; standard epoxy is walk-on in 24 hours and full traffic in 72 hours." },
            },
          ],
        }),
      },
    ],
  }),
  component: HomePage,
});


const audiences = ["Homeowners", "Barbershops", "Clinics", "Dealerships", "Showrooms", "Restaurants"];

function HomePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-hero">
        <div className="absolute inset-0 z-0">
          <img src={heroImg} alt="Metallic epoxy flooring" className="w-full h-full object-cover opacity-90 animate-ken-burns" width={1920} height={1280} fetchPriority="high" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-background/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-transparent" />
        </div>



        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="max-w-4xl mx-auto text-center animate-fade-up">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[0.95] mb-6">
              Epoxy Flooring in New Jersey<br />
              <em className="not-italic block mt-3 text-2xl sm:text-3xl lg:text-4xl bg-gradient-gold bg-clip-text text-transparent">Have a floor in mind? We'll pour it</em>

            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed">
              Blanco Pro Services delivers showroom-grade epoxy flooring, custom hardscaping, and precise demolition across New Jersey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/services" className="inline-flex items-center justify-center gap-2 bg-background/80 backdrop-blur px-8 py-4 rounded-full font-medium border border-border hover:bg-background transition">
                Explore Services
              </Link>
              <Link to="/contact" className="sheen group inline-flex items-center justify-center gap-2 bg-foreground text-background px-8 py-4 rounded-full font-medium shadow-elegant hover:shadow-gold hover:bg-gradient-gold hover:text-gold-foreground transition-all duration-300">
                Get Free Estimate <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>

            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
              {[
                { n: "Seamless", l: "Stain & Chemical Proof" },
                { n: "1-Day", l: "Install Turnaround" },
                { n: "5★", l: "Customer Service" },
                { n: "2-Year", l: "Workmanship Warranty" },
              ].map((s, i) => (
                <div
                  key={s.l}
                  className="text-center animate-fade-up"
                  style={{ animationDelay: `${400 + i * 90}ms`, animationFillMode: "both", opacity: 0 }}
                >
                  <div className="font-display text-2xl md:text-3xl font-bold bg-gradient-gold bg-clip-text text-transparent">{s.n}</div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{s.l}</div>
                </div>
              ))}
            </div>

          </div>
        </div>

        <div className="group absolute bottom-0 left-0 right-0 overflow-hidden py-4 border-y border-border bg-background/80 backdrop-blur z-10">
          <div className="flex gap-12 animate-marquee whitespace-nowrap group-hover:[animation-play-state:paused]">
            {[...audiences, ...audiences, ...audiences].map((a, i) => (
              <span key={i} className="font-display text-2xl text-muted-foreground/60 flex items-center gap-12">
                {a} <span className="text-gold">✦</span>
              </span>
            ))}
          </div>
        </div>

      </section>

      {/* EPOXY TYPES, RESIDENTIAL LEAD */}
      <section className="py-28 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16 max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Epoxy Flooring</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">Residential floors, reimagined.</h2>
              <p className="text-muted-foreground text-lg">From garage transformations to luxury living rooms, choose the epoxy system that fits your space and style.</p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 mb-28">
            {[
              {
                title: "Epoxy Flake (Chip) Floors",
                look: "Seamless concrete coated with multi-colored vinyl or acrylic flakes for a textured, granite-like appearance.",
                where: "Garages, basements, laundry rooms, and workshops.",
                why: "Highly durable, hides dirt easily, and provides a textured, slip-resistant surface.",
              },
              {
                title: "Metallic Epoxy Floors",
                look: "100% solids epoxy mixed with metallic pigments to create a 3D, swirling, marbled shimmering effect.",
                where: "Open-concept living rooms, modern kitchens, entryways, and bathrooms.",
                why: "Creates a dramatic, high-end, contemporary luxury aesthetic that turns the floor into the centerpiece of the room.",
              },
              {
                title: "Solid Color Epoxy",
                look: "A completely uniform, smooth, glossy or matte color. Pure white, soft gray, and beige are popular choices.",
                where: "Modern lofts, minimalist kitchens, studios, and contemporary homes.",
                why: "Offers a clean, seamless, hypoallergenic surface that is incredibly easy to sweep and mop.",
              },
            ].map((e, i) => (
              <Reveal key={e.title} delay={i * 120}>
                <article className="h-full rounded-2xl border border-border bg-card p-8 shadow-soft hover:shadow-elegant hover:-translate-y-1 transition-all">
                  <div className="text-xs uppercase tracking-[0.25em] text-gold mb-3">0{i + 1}</div>
                  <h3 className="font-display text-2xl font-bold mb-4">{e.title}</h3>
                  <div className="space-y-3 text-sm leading-relaxed">
                    <p><span className="font-semibold text-foreground">The Look: </span><span className="text-muted-foreground">{e.look}</span></p>
                    <p><span className="font-semibold text-foreground">Where to Use: </span><span className="text-muted-foreground">{e.where}</span></p>
                    <p><span className="font-semibold text-foreground">Why it's popular: </span><span className="text-muted-foreground">{e.why}</span></p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="text-center mb-16 max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">What We Do</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">Three crafts. One standard.</h2>
              <p className="text-muted-foreground text-lg">Every surface deserves precision. We bring the materials, the team, and the obsessive attention to finish.</p>
            </div>
          </Reveal>


          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, title: "Epoxy Flooring", img: metallicImg, alt: "Metallic epoxy flooring installation", desc: "Metallic, flake, polished concrete, and polyaspartic systems for every space." },
              { icon: Trees, title: "Hardscaping", img: hardscapeImg, alt: "Custom hardscaping and paver installation", desc: "Pavers, retaining walls, patios, and concrete work that elevates curb appeal." },
              { icon: Hammer, title: "Demolition", img: demoImg, alt: "Professional demolition and site preparation", desc: "Clean, controlled removal of concrete, structures, and prep work for new builds." },

            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 120}>
                <Link to="/services" className="block card-tilt rounded-2xl overflow-hidden bg-card border border-border group">
                  <div className="aspect-[4/3] overflow-hidden relative clip-reveal">
                    <img src={s.img} alt={s.alt} loading="lazy" decoding="async" width={800} height={600} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-background/90 backdrop-blur flex items-center justify-center shadow-soft">
                      <s.icon className="w-5 h-5" />
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="font-display text-2xl font-bold mb-2">{s.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">{s.desc}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground group-hover:gap-2 transition-all">
                      View {s.title.toLowerCase()} services <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8 bg-secondary overflow-hidden">
        {/* decorative gold glow */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-gradient-gold blur-3xl opacity-20" />
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-72 h-72 rounded-full bg-gradient-gold blur-3xl opacity-10" />

        <div className="relative max-w-7xl mx-auto">
          {/* Header row */}
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-20">
            <Reveal className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-gold" />
                <span className="text-[10px] uppercase tracking-[0.35em] text-gold font-medium">Why Blanco Pro</span>
              </div>
              <h2 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02]">
                Built on craft.<br />
                <em className="not-italic bg-gradient-gold bg-clip-text text-transparent">Delivered</em> on time.
              </h2>
            </Reveal>
            <Reveal delay={120} className="lg:col-span-5">
              <p className="text-muted-foreground text-lg leading-relaxed">
                We treat every project like our own home or business. From the prep to the final coat, our team obsesses over the details other contractors overlook, and the proof shows up in floors that still shine ten years later.
              </p>
            </Reveal>
          </div>


          {/* Reasons grid, numbered editorial */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, t: "Surface Prep Obsessed", d: "Diamond grinding, moisture testing, and crack repair before a drop of resin touches the slab. It's the #1 reason our floors outlast the competition." },
              { icon: Sparkles, t: "Premium Materials Only", d: "100% solids epoxies, aliphatic polyaspartics, and architectural pavers, backed by manufacturer warranties, never big-box kits." },
              { icon: Star, t: "Showroom-Grade Finish", d: "Every project ends with a final walk-through under daylight and overhead lighting. If a single flake is out of place, we fix it." },
              { icon: Hammer, t: "On-Time, On-Budget", d: "Written quotes, locked timelines, and phased installs that keep your home or facility running. No mystery change orders." },
              { icon: Trees, t: "Local NJ Crew", d: "Serving New Jersey, Pennsylvania, and New York. We answer the phone, show up when we say we will, and stand behind every install." },
              { icon: CheckCircle2, t: "Clear From Day One", d: "Free consultation, a detailed written quote, and a single point of contact from the first call through the final walk-through." },
            ].map((r, i) => (
              <Reveal key={r.t} delay={(i % 3) * 100}>
                <article className="group relative h-full rounded-3xl border border-border bg-background p-8 shadow-soft hover:shadow-elegant hover:-translate-y-1 transition-all overflow-hidden">
                  {/* oversized ghost numeral */}
                  <div className="absolute -top-4 -right-2 font-display text-[7rem] leading-none font-bold text-gold/10 select-none pointer-events-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-gold flex items-center justify-center mb-5 shadow-gold">
                      <r.icon className="w-5 h-5 text-gold-foreground" />
                    </div>
                    <h3 className="font-display text-xl font-bold mb-3">{r.t}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{r.d}</p>
                    <div className="mt-6 h-px w-0 bg-gradient-gold group-hover:w-full transition-all duration-500" />
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* INDUSTRY SOURCES / PARTNERS */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-background border-t border-border">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Built On NJ Chemistry</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">Engineered with New Jersey's best resin houses.</h2>
              <p className="text-muted-foreground text-lg">
                We spec and source from established New Jersey epoxy manufacturers, proven chemistry from people who literally invent the resins our floors are built on.
              </p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { n: "Master Bond, Inc.", loc: "Hackensack, NJ", d: "High-performance epoxy adhesives, sealants, coatings, and encapsulation systems for complex engineering requirements." },
              { n: "A & C Catalysts", loc: "Linden, NJ", d: "Specialty epoxy resins, curing agents, latent catalysts, and flame retardants for automotive and industrial use." },
              { n: "Innovative Resin Systems", loc: "Newark, NJ", d: "Custom epoxy and polyurethane resin systems formulated for industrial bonding and structural applications." },
              { n: "John C. Dolph Company", loc: "Monmouth Junction, NJ", d: "Insulating varnishes and epoxy resins engineered for electrical and electronics manufacturing." },
              { n: "Cardolite Corporation", loc: "Monmouth Junction, NJ", d: "Bio-based epoxy curing agents and resins derived from cashew nut shell liquid, sustainable performance chemistry." },
              { n: "Local NJ supply chain", loc: "Statewide", d: "Sourcing within New Jersey keeps lead times short, supports local manufacturing, and guarantees fresh, properly stored chemistry on every install." },
            ].map((p, i) => (
              <Reveal key={p.n} delay={(i % 3) * 100}>
                <div className="h-full p-6 rounded-2xl border border-border bg-card card-tilt">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-gold mb-2">{p.loc}</div>
                  <h3 className="font-display text-lg font-bold mb-2">{p.n}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="text-xs text-muted-foreground/70 text-center mt-8 max-w-2xl mx-auto">
              References to third-party manufacturers reflect the New Jersey epoxy ecosystem we operate within. Blanco Pro Services is independent and not endorsed by these companies.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-28 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Client Stories</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold">Trusted across New Jersey</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { q: "Our showroom floor is the first thing every customer compliments. Flawless work.", a: "Marco R.", r: "Dealership Owner" },
              { q: "They turned our shop floor into a mirror. Booked them again for our second location.", a: "Devon T.", r: "Barbershop Owner" },
              { q: "Professional, on schedule, and the patio looks straight out of a magazine.", a: "Sarah K.", r: "Homeowner" },
            ].map((t, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="card-tilt bg-card border border-border rounded-2xl p-8 h-full">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-gold text-gold" />)}
                  </div>
                  <p className="font-display text-lg leading-relaxed mb-6">"{t.q}"</p>
                  <div>
                    <div className="font-semibold">{t.a}</div>
                    <div className="text-sm text-muted-foreground">{t.r}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
